// Gestione allegati condivisa (dettaglio ticket e nuovo ticket)
import { supabase } from './supabase'

export const MAX_FILE_MB = 25
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024
const SIGN_SECONDS = 6 * 3600

// Immagini che i browser sanno mostrare (le HEIC dell'iPhone, ad esempio, no: restano scaricabili)
const PREVIEWABLE = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/avif']
export const isPreviewable = (type) => PREVIEWABLE.includes((type || '').toLowerCase())

// Nome sicuro per lo storage: niente accenti, simboli o spazi (Supabase rifiuta le chiavi non ASCII)
export function storageSafeName(name) {
  const base = (name || 'file').normalize('NFD').replace(/[̀-ͯ]/g, '')
  const dot = base.lastIndexOf('.')
  const stem = (dot > 0 ? base.slice(0, dot) : base).replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 80) || 'file'
  const ext = dot > 0 ? base.slice(dot + 1).replace(/[^A-Za-z0-9]+/g, '').slice(0, 10) : ''
  return ext ? `${stem}.${ext}` : stem
}

const rand = () => Math.random().toString(36).slice(2, 8)
export const storagePath = (ticketId, name) => `${ticketId}/${Date.now()}_${rand()}_${storageSafeName(name)}`

// Le immagini incollate dagli appunti arrivano tutte come "image.png": diamo un nome riconoscibile
export function namePastedFile(file) {
  if (file.name && file.name !== 'image.png' && file.name !== 'image.jpeg') return file
  const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg')
  const stamp = new Date().toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace(/[/:, ]+/g, '-')
  return new File([file], `immagine-incollata-${stamp}.${ext}`, { type: file.type })
}

// File dagli appunti (Ctrl+V di uno screenshot o di file copiati)
export function filesFromClipboard(e) {
  const items = Array.from(e.clipboardData?.items || [])
  return items.filter(i => i.kind === 'file').map(i => i.getAsFile()).filter(Boolean).map(namePastedFile)
}

// Controlli prima dell'invio: restituisce l'elenco dei problemi (vuoto = tutto ok)
export function checkFiles(files) {
  const problems = []
  for (const f of files) {
    if (!f.size) problems.push(`"${f.name}" è vuoto`)
    else if (f.size > MAX_FILE_BYTES) problems.push(`"${f.name}" supera ${MAX_FILE_MB} MB`)
  }
  return problems
}

const sleep = (ms) => new Promise(r => setTimeout(r, ms))

// Carica i file nello storage (con un nuovo tentativo in caso di errore di rete).
// Non registra nulla nel database: se anche un solo file fallisce, rimuove quelli gia' caricati.
export async function uploadToStorage(ticketId, files) {
  const done = []
  for (const file of files) {
    const path = storagePath(ticketId, file.name)
    let error = null
    for (let attempt = 0; attempt < 2; attempt++) {
      ;({ error } = await supabase.storage.from('attachments').upload(path, file, { contentType: file.type || 'application/octet-stream' }))
      if (!error) break
      await sleep(700)
    }
    if (error) {
      if (done.length) await supabase.storage.from('attachments').remove(done.map(d => d.path)).catch(() => {})
      return { uploaded: [], error: `"${file.name}": ${error.message || 'caricamento non riuscito'}` }
    }
    done.push({ file, path })
  }
  return { uploaded: done, error: null }
}

// Registra nel database i file gia' caricati
export async function registerAttachments({ ticketId, commentId, uploaded, userId }) {
  if (!uploaded.length) return { rows: [], error: null }
  const { data, error } = await supabase.from('attachments').insert(uploaded.map(u => ({
    ticket_id: ticketId,
    comment_id: commentId || null,
    file_name: u.file.name,
    file_path: u.path,
    file_size: u.file.size,
    file_type: u.file.type || null,
    uploaded_by: userId,
  }))).select()
  if (error) {
    // Non registrati = invisibili nel ticket: si tolgono dallo storage per non lasciare file orfani
    await supabase.storage.from('attachments').remove(uploaded.map(u => u.path)).catch(() => {})
    return { rows: [], error: error.message }
  }
  return { rows: data || [], error: null }
}

// URL firmato per l'anteprima
export async function signedUrl(path) {
  const { data } = await supabase.storage.from('attachments').createSignedUrl(path, SIGN_SECONDS)
  return data?.signedUrl || null
}

// Scarica il file col suo nome originale (l'attributo download non vale per domini esterni)
export async function downloadAttachment(att) {
  const { data, error } = await supabase.storage.from('attachments')
    .createSignedUrl(att.file_path, 600, { download: att.file_name || true })
  if (error || !data?.signedUrl) throw new Error(error?.message || 'File non trovato')
  const a = document.createElement('a')
  a.href = data.signedUrl
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

// Esito della chiamata al server email: lancia un errore leggibile se l'email non e' partita
export async function sendEmail(payload) {
  // Il server invia solo per conto di un operatore entrato: gli si passa il token della sessione
  const { data: { session } } = await supabase.auth.getSession()
  const headers = { 'Content-Type': 'application/json' }
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`
  let res
  try {
    res = await fetch('/api/send-email', { method: 'POST', headers, body: JSON.stringify(payload) })
  } catch (e) {
    throw new Error('server email non raggiungibile')
  }
  const out = await res.json().catch(() => ({}))
  if (!res.ok || out.ok === false) throw new Error(out.error || `errore ${res.status}`)
  return out
}
